import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCommerceInlineSectionExpand } from '../utils/commerceSectionExpand';
import Icon from '../../../components/AppIcon';
import { createWebCreatorReservationRequest } from '../../../api/webCreator';
import CommerceMonthCalendar from './CommerceMonthCalendar';
import './RestaurantReservation.css';
import ReservationClassicEmbedded from './ReservationClassicEmbedded';
import {
  buildReservationTimeSlots,
  commerceDisplayColor,
  earliestReservationDate,
  filterTimeSlotsForDate,
  formatPreferredDateIso,
  resolveReservationDisplay,
  resolveReservationSubmitLabel,
  remainingCoversOnDay,
  dayKeyFromDate,
} from '../utils/commerceDisplay';

import {
  hasConfiguredServiceWeek,
  isServiceDayOpen,
  weekdayKeyFromDate,
} from '../utils/commerceAdmin';

const publicInputClass =
  'mt-2 w-full rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-300/80';

const DEFAULT_RESTAURANT_COPY = {
  eyebrow: 'VOTRE PROCHAINE BELLE TABLE',
  titleHtml: 'On vous<br>garde une <em>place ?</em>',
  lead: 'Choisissez votre moment, nous nous occupons du reste.',
  noteTitle: 'Une occasion spéciale ?',
  noteText:
    'Anniversaire, dîner à deux ou déjeuner entre amis : dites-le-nous dans votre demande.',
  bookingKicker: 'RÉSERVATION EN LIGNE',
  bookingTitle: 'Votre table, en quelques clics.',
};

export default function ReservationPublicView({
  slug,
  site,
  siteTitle,
  reservation = {},
  onBack,
  embedded = false,
  variant = 'default',
  reservationCopy,
  layoutMode,
  interactive = true,
  commerceExpandCompId,
  commerceExpandSlotHeightPx = 0,
}) {
  const expandRootRef = useRef(null);
  const commerceExpandEnabled =
    embedded && Boolean(commerceExpandCompId) && Number(commerceExpandSlotHeightPx) > 0;
  useCommerceInlineSectionExpand({
    compId: commerceExpandCompId,
    enabled: commerceExpandEnabled,
    slotHeightPx: commerceExpandSlotHeightPx,
    rootRef: expandRootRef,
  });
  const display = useMemo(
    () => resolveReservationDisplay(site || { reservation, title: siteTitle }),
    [site, reservation, siteTitle],
  );

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const copy = { ...DEFAULT_RESTAURANT_COPY, ...(reservationCopy || {}) };
  const restaurantEmbeddedCard = variant === 'restaurant' && embedded;

  const maxParty = Math.max(1, Number(reservation.maxPartySize) || 6);
  const leadTimeHours = Math.max(0, Number(reservation.leadTimeHours) || 2);
  const coversPerDay = Math.max(1, Number(reservation.coversPerDay) || 40);
  const dayBookings = site?.reservationDayBookings || {};
  const serviceWeek = site?.reservation?.serviceWeek;
  const minDate = useMemo(() => earliestReservationDate(leadTimeHours), [leadTimeHours]);
  const availabilityConfigured = useMemo(
    () => hasConfiguredServiceWeek(serviceWeek),
    [serviceWeek],
  );

  const isDayDisabled = useMemo(
    () => (date) => {
      if (!availabilityConfigured) return true;
      if (!isServiceDayOpen(serviceWeek, date)) return true;
      const key = dayKeyFromDate(date);
      return remainingCoversOnDay(key, coversPerDay, dayBookings, partySize) < partySize;
    },
    [availabilityConfigured, serviceWeek, coversPerDay, dayBookings, partySize],
  );

  const allSlots = useMemo(() => buildReservationTimeSlots(), []);
  const daySlot = useMemo(() => {
    if (!selectedDate || serviceWeek == null) return null;
    const key = weekdayKeyFromDate(selectedDate);
    return serviceWeek[key] || null;
  }, [selectedDate, serviceWeek]);

  const availableSlots = useMemo(
    () => filterTimeSlotsForDate(allSlots, selectedDate, leadTimeHours, daySlot),
    [allSlots, selectedDate, leadTimeHours, daySlot],
  );

  useEffect(() => {
    if (timeSlot && !availableSlots.includes(timeSlot)) {
      setTimeSlot('');
    }
  }, [timeSlot, availableSlots]);

  const accent = commerceDisplayColor(display.accentColor, '#0f766e');
  const bg = commerceDisplayColor(display.backgroundColor, '#f8fafc');
  const btnBg = commerceDisplayColor(display.buttonColor, accent);
  const btnText = commerceDisplayColor(display.buttonTextColor, '#ffffff');
  const submitLabel = resolveReservationSubmitLabel(display, {
    busy,
    interactive,
    variant,
  });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!interactive || !slug) {
      setError('Réservation disponible sur la page publiée.');
      return;
    }
    if (!name.trim()) {
      setError('Indiquez votre nom.');
      return;
    }
    if (!selectedDate || !timeSlot) {
      setError('Choisissez une date et un horaire.');
      return;
    }
    if (variant === 'restaurant' && !consent) {
      setError('Veuillez accepter le traitement de vos données.');
      return;
    }
    setBusy(true);
    try {
      await createWebCreatorReservationRequest(slug, {
        customerName: name.trim(),
        customerEmail: email.trim(),
        customerPhone: phone.trim(),
        partySize,
        preferredDate: formatPreferredDateIso(selectedDate, timeSlot),
        note: note.trim(),
      });
      setDone(true);
    } catch (err) {
      setError(err?.message || 'Demande impossible.');
    } finally {
      setBusy(false);
    }
  };

  const adjustParty = (delta) => {
    setPartySize((p) => Math.max(1, Math.min(maxParty, p + delta)));
  };

  const restaurantBookingCard = (
    <div
      className={`jop-restaurant-booking${
        layoutMode === 'booking-card' ? ' jop-restaurant-booking--kit-cell' : ''
      }`}
      style={{ '--forest': accent }}
    >
            {done ? (
              <div className="text-center space-y-3 py-6">
                <div className="mx-auto w-12 h-12 rounded-full bg-[#e8eee3] flex items-center justify-center text-[#203b2d]">
                  <Icon name="Check" size={24} />
                </div>
                <h3 className="font-serif text-xl">C&apos;est noté</h3>
                <p className="text-sm text-[#4c594a]">
                  Merci {name || ''} pour votre réservation.
                  {partySize > 1 ? 's' : ''}.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="jop-restaurant-booking__form">
                <div className="jop-restaurant-booking__head">
                  <div>
                    <span className="jop-restaurant-booking__kicker">{copy.bookingKicker}</span>
                    <h3>{display.formTitle || copy.bookingTitle}</h3>
                  </div>
                  <span className="jop-restaurant-booking__icon jardins-picto" aria-hidden="true" />
                </div>

                <div className="jop-restaurant-booking__picker-row">
                  <label className="jop-restaurant-booking__guests">
                    Nombre de convives
                    <select
                      value={partySize}
                      onChange={(e) => setPartySize(Number(e.target.value) || 1)}
                      disabled={!interactive}
                    >
                      {Array.from({ length: maxParty }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>
                          {n} personne{n > 1 ? 's' : ''}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="jop-restaurant-booking__date-label">
                    Date souhaitée
                    {!availabilityConfigured ? (
                      <p className="text-[10px] font-normal text-[#898f84] mt-2 mb-1 leading-snug">
                        Configurez les jours et horaires dans l&apos;administration commerce pour
                        activer le calendrier.
                      </p>
                    ) : null}
                    <div
                      className={
                        !availabilityConfigured ? 'jop-restaurant-calendar--disabled' : undefined
                      }
                    >
                      <CommerceMonthCalendar
                        className="jop-restaurant-calendar"
                        size="large"
                        value={selectedDate}
                        onChange={(d) => {
                          setError('');
                          if (!d) {
                            setSelectedDate(null);
                            setTimeSlot('');
                            return;
                          }
                          if (isDayDisabled(d)) {
                            setError('Cette date n’est pas disponible — choisissez un autre jour.');
                            return;
                          }
                          setSelectedDate(d);
                          setTimeSlot('');
                        }}
                        minDate={minDate}
                        accentColor={commerceDisplayColor(display.accentColor, '#203b2d')}
                        isDayDisabled={isDayDisabled}
                      />
                    </div>
                  </label>
                </div>

                {selectedDate ? (
                  <fieldset className="jop-restaurant-booking__times">
                    <legend>Choisissez votre service</legend>
                    {availableSlots.length === 0 ? (
                      <p className="text-xs text-[#898f84] py-2">
                        Aucun créneau ce jour — choisissez une autre date.
                      </p>
                    ) : (
                      <div className="time-options">
                        {availableSlots.map((slot) => (
                          <label key={slot} className="time-option">
                            <input
                              type="radio"
                              name="time"
                              value={slot}
                              checked={timeSlot === slot}
                              onChange={() => setTimeSlot(slot)}
                            />
                            <span>{slot}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </fieldset>
                ) : null}

                <div className="form-row">
                  <label>
                    Votre nom
                    <input
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Prénom et nom"
                      autoComplete="name"
                    />
                  </label>
                  <label>
                    Votre e-mail
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.fr"
                      autoComplete="email"
                    />
                  </label>
                </div>
                <label>
                  Téléphone
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="06 …"
                    autoComplete="tel"
                  />
                </label>
                <label>
                  Une précision ? <span className="text-[#92978d] font-normal">(facultatif)</span>
                  <textarea
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Allergies, occasion spéciale…"
                  />
                </label>
                <label className="consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  <span>
                    J&apos;accepte que mes informations soient utilisées pour traiter ma demande de
                    réservation.
                  </span>
                </label>
                {error ? <p className="text-xs text-red-600 mb-2">{error}</p> : null}
                {!interactive ? (
                  <p className="text-[10px] text-[#898f84] mb-2">
                    Aperçu éditeur — la réservation fonctionne sur le site publié.
                  </p>
                ) : null}
                <button
                  type="submit"
                  className="jop-restaurant-booking__submit"
                  disabled={busy || !interactive || !availabilityConfigured}
                  style={{ backgroundColor: btnBg, color: btnText }}
                >
                  {submitLabel}{' '}
                  <span className="jardins-arrow-ne" aria-hidden="true" />
                </button>
              </form>
            )}
    </div>
  );

  if (restaurantEmbeddedCard) {
    return (
      <div ref={expandRootRef} className="w-full min-h-0 overflow-visible box-border">
        {restaurantBookingCard}
      </div>
    );
  }

  if (variant === 'default' && embedded) {
    return (
      <div
        ref={expandRootRef}
        className="w-full min-h-0 overflow-visible box-border"
        style={{ '--classic-res-accent': accent }}
      >
        <ReservationClassicEmbedded
          display={display}
          accent={accent}
          btnBg={btnBg}
          btnText={btnText}
          done={done}
          name={name}
          partySize={partySize}
          maxParty={maxParty}
          setPartySize={setPartySize}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          setTimeSlot={setTimeSlot}
          setError={setError}
          minDate={minDate}
          availabilityConfigured={availabilityConfigured}
          isDayDisabled={isDayDisabled}
          timeSlot={timeSlot}
          availableSlots={availableSlots}
          email={email}
          setEmail={setEmail}
          phone={phone}
          setPhone={setPhone}
          setName={setName}
          note={note}
          setNote={setNote}
          error={error}
          busy={busy}
          interactive={interactive}
          submit={submit}
          submitLabel={submitLabel}
        />
      </div>
    );
  }

  if (variant === 'restaurant') {
    return (
      <div className="jop-restaurant-reservation w-full">
        <div className="jop-restaurant-reservation__backdrop" aria-hidden />
        <div className="jop-restaurant-reservation__inner">
          <div className="jop-restaurant-reservation__copy">
            <p className="jop-restaurant-reservation__eyebrow">
              <span /> {copy.eyebrow}
            </p>
            <h2
              className="jop-restaurant-reservation__title"
              dangerouslySetInnerHTML={{ __html: copy.titleHtml }}
            />
            <p className="jop-restaurant-reservation__lead">{copy.lead}</p>
            <div className="jop-restaurant-reservation__note">
              <span className="jardins-picto" aria-hidden="true" />
              <div>
                <b>{copy.noteTitle}</b>
                <small>{copy.noteText}</small>
              </div>
            </div>
          </div>
          {restaurantBookingCard}
        </div>
      </div>
    );
  }

  return (
    <div
      className={embedded ? 'w-full text-stone-900' : 'min-h-screen text-stone-900'}
      style={{ backgroundColor: bg }}
    >
      {!embedded ? (
      <header className="sticky top-0 z-20 border-b border-stone-200/80 bg-white/95 backdrop-blur-md">
        <div className="max-w-xl mx-auto px-4 py-4 relative flex items-center justify-center">
          <button
            type="button"
            className="absolute left-2 sm:left-4 p-2 rounded-lg hover:bg-stone-100"
            onClick={onBack}
          >
            <Icon name="ArrowLeft" size={20} />
          </button>
          <div className="text-center px-12 min-w-0">
            <p
              className="text-[10px] font-semibold uppercase tracking-[0.25em]"
              style={{ color: accent }}
            >
              Réserver une table
            </p>
            <h1 className="font-serif text-xl font-semibold truncate text-stone-900">
              {siteTitle || 'Votre établissement'}
            </h1>
          </div>
        </div>
      </header>
      ) : null}

      <div className={`${embedded ? 'w-full max-w-3xl mx-auto' : 'max-w-xl mx-auto'} px-4 py-8 sm:py-10 w-full`}>
        {reservation.introHtml ? (
          <div
            className="prose prose-sm max-w-none mb-8 text-stone-700 text-center mx-auto"
            dangerouslySetInnerHTML={{ __html: reservation.introHtml }}
          />
        ) : null}

        {done ? (
          <div
            className="rounded-2xl border p-8 text-center space-y-3 shadow-sm bg-white"
            style={{ borderColor: `${accent}40` }}
          >
            <div
              className="mx-auto w-14 h-14 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${accent}15`, color: accent }}
            >
              <Icon name="Check" size={28} />
            </div>
            <h2 className="font-serif text-xl font-semibold">C&apos;est noté</h2>
            <p className="text-sm text-stone-600 max-w-md mx-auto">
              Merci {name || ''} pour votre réservation.
              {partySize} convive{partySize > 1 ? 's' : ''}.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-8">
            <div className="text-center">
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-stone-900">
                {display.formTitle}
              </h2>
              {display.formSubtitle ? (
                <p className="text-sm text-stone-600 mt-2">{display.formSubtitle}</p>
              ) : null}
            </div>

            <div className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6 shadow-sm space-y-5">
              <label className="block text-left">
                <span className="text-xs font-medium text-stone-600">Nom et prénom</span>
                <input
                  className={`${publicInputClass} text-base font-medium`}
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Prénom et nom"
                  autoComplete="name"
                />
              </label>

              <div className="text-left">
                <span className="text-xs font-medium text-stone-600">Nombre de convives</span>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 rounded-xl border border-stone-200 p-1">
                    <button
                      type="button"
                      className="h-10 w-10 rounded-lg hover:bg-stone-100 text-lg"
                      onClick={() => adjustParty(-1)}
                      aria-label="Moins de personnes"
                    >
                      −
                    </button>
                    <span className="min-w-[3rem] text-center text-xl font-semibold tabular-nums">
                      {partySize}
                    </span>
                    <button
                      type="button"
                      className="h-10 w-10 rounded-lg text-white text-lg"
                      style={{ backgroundColor: accent }}
                      onClick={() => adjustParty(1)}
                      aria-label="Plus de personnes"
                    >
                      +
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {quickPartySizes(maxParty).map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setPartySize(n)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                          partySize === n
                            ? 'text-white border-transparent'
                            : 'border-stone-200 text-stone-700 hover:bg-stone-50'
                        }`}
                        style={partySize === n ? { backgroundColor: accent } : undefined}
                      >
                        {n} pers.
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-stone-500 w-full">Jusqu&apos;à {maxParty} convives.</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block text-left">
                  <span className="text-xs font-medium text-stone-600">E-mail</span>
                  <input
                    type="email"
                    className={publicInputClass}
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.fr"
                  />
                </label>
                <label className="block text-left">
                  <span className="text-xs font-medium text-stone-600">Téléphone</span>
                  <input
                    className={publicInputClass}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="06 …"
                  />
                </label>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-serif text-base font-semibold text-center text-stone-800">
                Choisir une date
              </h3>
              <CommerceMonthCalendar
                value={selectedDate}
                onChange={(d) => {
                  setSelectedDate(d);
                  setTimeSlot('');
                }}
                minDate={minDate}
                accentColor={accent}
                isDayDisabled={isDayDisabled}
              />
            </div>

            {selectedDate ? (
              <div className="space-y-3">
                <h3 className="font-serif text-base font-semibold text-center text-stone-800">
                  Choisir un horaire
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {availableSlots.length === 0 ? (
                    <p className="col-span-full text-sm text-stone-500 text-center py-4">
                      Aucun créneau disponible ce jour — choisissez une autre date.
                    </p>
                  ) : (
                    availableSlots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setTimeSlot(slot)}
                        className={`py-2.5 rounded-xl text-sm font-medium border transition-all ${
                          timeSlot === slot
                            ? 'text-white shadow-md border-transparent'
                            : 'border-stone-200 bg-white hover:border-stone-300 text-stone-800'
                        }`}
                        style={
                          timeSlot === slot ? { backgroundColor: accent } : undefined
                        }
                      >
                        {slot}
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : null}

            <label className="block rounded-2xl border border-stone-200 bg-white p-5 shadow-sm text-left">
              <span className="text-xs font-medium text-stone-600">Message</span>
              <textarea
                className={`${publicInputClass} min-h-[88px]`}
                placeholder="Occasion, allergies, demande de table en terrasse…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>

            {error ? <p className="text-sm text-red-600 text-center">{error}</p> : null}

            <button
              type="submit"
              disabled={busy || !interactive}
              className="w-full max-w-md mx-auto block rounded-xl py-3.5 text-sm font-semibold shadow-lg transition-opacity disabled:opacity-60"
              style={{ backgroundColor: btnBg, color: btnText }}
            >
              {!interactive && !busy
                ? String(display.submitButtonLabelPreview || '').trim() || 'Aperçu éditeur'
                : submitLabel}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function quickPartySizes(max) {
  const sizes = [2, 4, 6, 8].filter((n) => n <= max);
  if (!sizes.includes(max) && max > 0) sizes.push(max);
  return [...new Set(sizes)].sort((a, b) => a - b);
}
